import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Trash2, Pencil, Send } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { motion } from 'framer-motion';
import EditNoteDialog from '@/components/EditNoteDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function NotesPage() {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingNote, setEditingNote] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [sendingNoteId, setSendingNoteId] = useState(null);

  useEffect(() => {
    fetchNotes();
  }, []);

  const fetchNotes = async () => {
    try {
      const response = await axios.get(`${API_URL}/notes`);
      setNotes(response.data);
    } catch (error) {
      console.error('Errore nel caricamento note:', error);
      toast.error('Errore nel caricamento delle note');
    } finally {
      setLoading(false);
    }
  };

  const handleEditNote = (note) => {
    setEditingNote(note);
    setShowEditDialog(true);
  };

  const handleCloseEdit = () => {
    setShowEditDialog(false);
    setEditingNote(null);
  };

  const handleSaveEdit = () => {
    fetchNotes();
  };

  const handleSendNow = async (noteId) => {
    setSendingNoteId(noteId);
    try {
      await axios.post(`${API_URL}/notes/${noteId}/send-now`);
      toast.success('✉️ Nota inviata immediatamente!');
      fetchNotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'invio');
    } finally {
      setSendingNoteId(null);
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await axios.delete(`${API_URL}/notes/${noteId}`);
      toast.success('Nota eliminata');
      fetchNotes();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <>
      <div className="flex-1 p-4 md:p-8 overflow-y-auto pb-24 md:pb-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-4xl mx-auto space-y-8"
        >
          <div>
            <h1 className="text-4xl font-bold tracking-tight mb-2">Le tue note</h1>
            <p className="text-muted-foreground">
              Tutte le note vocali e testuali
            </p>
          </div>

          {notes.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <p className="text-lg">Nessuna nota ancora.</p>
                <p className="text-sm mt-2">Registra la tua prima nota vocale o scrivi una nota testuale!</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {notes.map((note, index) => (
                <motion.div
                  key={note.id || index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card className="hover:shadow-lg transition-shadow group" data-testid={`note-card-${index}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 space-y-3">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant="secondary">{note.category}</Badge>
                            {note.is_sent ? (
                              <Badge variant="default" className="bg-green-600 hover:bg-green-700">
                                ✓ Inviata
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-orange-600 border-orange-600">
                                ⏳ Da inviare
                              </Badge>
                            )}
                            <span className="text-sm text-muted-foreground">
                              {formatDate(note.created_at)}
                            </span>
                          </div>
                          <p className="text-sm whitespace-pre-wrap leading-relaxed">{note.content}</p>
                        </div>
                        <div className="flex gap-2 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity flex-shrink-0">
                          {!note.is_sent && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleSendNow(note.id)}
                              disabled={sendingNoteId === note.id}
                              className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                              data-testid={`send-now-${index}`}
                              title="Invia ora"
                            >
                              {sendingNoteId === note.id ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-600"></div>
                              ) : (
                                <Send className="w-4 h-4" />
                              )}
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEditNote(note)}
                            className="text-primary hover:text-primary hover:bg-primary/10"
                            data-testid={`edit-note-${index}`}
                            title="Modifica nota"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteNote(note.id)}
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            data-testid={`delete-note-${index}`}
                            title="Elimina nota"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </div>

      <EditNoteDialog
        open={showEditDialog}
        onClose={handleCloseEdit}
        note={editingNote}
        onSave={handleSaveEdit}
      />
    </>
  );
}